import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
// import NProgress from 'nprogress';
import EventBus from '@/utilities/event-bus';
import { authApi } from '@/utilities/axios';
import { showToast } from '@/utilities/toast-handler';
import { useAuthStore } from '@/modules/auth/store';

const APPLIANCE_URL = process.env.VUE_APP_API_VERSION + '/appliance';
export const useApplianceStore = defineStore('appliance', () => {
    const authStore = useAuthStore();
    const company_id = computed(() => {
        return authStore.getLoggedUserCompanyId;
    });

    const sortParams = ref('name asc');
    //state
    const appliance = ref({
        name: null,
        company_id: null,
    });

    const appliances = ref([]);
    const loading = ref(false);
    const editId = ref(null);

    //actions
    const fetchAllAppliances = async () => {
        try {
            // NProgress.start();
            loading.value = true;
            const response = await authApi.get(APPLIANCE_URL, {
                params: {
                    company_id: company_id.value,
                    sort: sortParams.value,
                },
            });
            if (response.statusCode === 200) {
                appliances.value = response.data;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch appliances');
        } finally {
            // NProgress.done();
            loading.value = false;
        }
    };

    const createAppliance = async () => {
        try {
            EventBus.emit('show-operation-loader');
            const bodyParams = { ...appliance.value, company_id: company_id.value };
            const response = await authApi.post(APPLIANCE_URL, bodyParams);
            if (response.statusCode === 201) {
                // push the data into the appliances.value
                appliances.value?.push(response.data);
                showToast(response.toast, 'New Appliance created successfully');
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to create appliance');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const updateAppliance = async () => {
        try {
            EventBus.emit('show-operation-loader');
            const bodyParams = { ...appliance.value, company_id: company_id.value };
            const response = await authApi.patch(`${APPLIANCE_URL}/${editId.value}`, bodyParams);
            if (response.statusCode === 200) {
                const existingAppliance = appliances.value.find((appliance) => appliance.id === editId.value);
                if (existingAppliance) {
                    const existingApplianceIndex = appliances.value.indexOf(existingAppliance);
                    if (existingApplianceIndex !== -1) {
                        // replace the updated data into the appliances.value index
                        appliances.value[existingApplianceIndex] = response.data;
                        showToast(response.toast, 'Appliance updated successfully');
                        return true;
                    }
                }
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to update appliance');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const deleteAppliance = async (applianceId) => {
        try {
            EventBus.emit('show-operation-loader');
            const response = await authApi.delete(`${APPLIANCE_URL}/${applianceId}`, {
                params: {
                    company_id: company_id.value,
                },
            });
            if (response.statusCode === 200) {
                // remove it from the array after deletion
                appliances.value = appliances.value?.filter((appliance) => appliance.id !== applianceId);
                showToast(response.toast, 'Appliance deleted successfully');
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to delete appliance');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const addApplianceFromJson = async (applianceJson) => {
        let postCount = 0;
        for (let i = 0; i < applianceJson.length; i++) {
            await authApi.post(APPLIANCE_URL, applianceJson[i]).then((res) => {
                if (res.statusCode === 201) {
                    postCount++;
                    appliances.value?.push(res.data);
                }
                if (postCount === applianceJson.length) {
                    showToast(res.toast, `${postCount} appliances added successfully`);
                    return true;
                }
            });
        }
    };

    // helpers
    const initAppliance = () => {
        appliance.value.name = null;
        appliance.value.company_id = null;
        editId.value = null;
    };

    const loadApplianceBody = (applianceData) => {
        editId.value = applianceData?.id;
        appliance.value.name = applianceData?.name;
    };

    return {
        company_id,
        sortParams,
        //states
        appliance,
        appliances,
        editId,

        //actions
        fetchAllAppliances,
        createAppliance,
        updateAppliance,
        deleteAppliance,
        addApplianceFromJson,

        //helpers
        initAppliance,
        loadApplianceBody,
    };
});
